import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Button, Container, Typography } from "@mui/material";
import useBreakpoints from "../../../hooks/useBreakpoints";
import { useAuth0 } from "@auth0/auth0-react";
import { APP_DETAILS, ROUTES } from "../../../constants";
import { useHistory } from "react-router-dom";

// Styled Components
const HeroContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "50vh",
  backgroundImage: "url(/static/img/landing/hero.jpg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    minHeight: "570px",
  },
}));

const Overlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
});

const StyledLogo = styled("img")(({ theme }) => ({
  height: "9rem",
  marginRight: theme.spacing(4),
  marginBottom: 0,
  [theme.breakpoints.down("md")]: {
    height: "6rem",
    marginRight: 0,
    marginBottom: theme.spacing(4),
  },
}));

const ContentContainer = styled(Container)({
  padding: "32px 0",
  zIndex: 2,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const Title = styled(Typography)(({ theme }) => ({
  textTransform: "uppercase",
  letterSpacing: "6px",
  maxWidth: "700px",
  color: "#eee",
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(1),
  },
}));

const HorizontalLine = styled("div")({
  width: "75%",
  height: "3px",
  backgroundColor: "white",
  margin: "48px auto 64px auto",
});

const Headline = styled(Typography)({
  textTransform: "uppercase",
  maxWidth: "800px",
  letterSpacing: "7px",
  lineHeight: "2",
  color: "#eee",
});

const Hero = () => {
  const { isXs, isSm } = useBreakpoints();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const history = useHistory();

  const handleButtonClick = () => {
    if (isAuthenticated) {
      history.push(ROUTES.MAP);
    } else {
      loginWithRedirect({ appState: { returnTo: ROUTES.MAP } });
    }
  };

  const buttonText = isAuthenticated
    ? isXs
      ? "Dashboard"
      : "View Dashboard"
    : isXs
    ? "Log in"
    : "Log in to Dashboard";

  return (
    <HeroContainer>
      <Overlay />
      <ContentContainer>
        <Box
          display="flex"
          flexDirection={isSm ? "column" : "row"}
          alignItems="center"
          justifyContent="center"
        >
          <StyledLogo
            src={APP_DETAILS.ICONS.LOGO_SIMPLE_WHITE}
            alt="App Logo"
          />
          <Title variant={isSm ? "h2" : "h1"} sx={{ fontWeight: "bold" }}>
            {APP_DETAILS.NAME}
          </Title>
        </Box>
        <HorizontalLine />
        <Headline variant="h4">
          Welcome to our new Management System! Log in to report your well meter
          readings or explore public forms to apply for new wells.
        </Headline>
        <Box
          mt={{ xs: 7, md: 12 }}
          display="flex"
          gap={2}
          flexDirection={isSm ? "column" : "row"}
          justifyContent="center"
        >
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              color: "white",
              border: "2px solid",
              borderColor: "secondary.main",
              "&:hover": {
                borderColor: "secondary.dark",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
            onClick={handleButtonClick}
            size={isXs ? "small" : isSm ? "medium" : "large"}
          >
            {buttonText}
          </Button>

          {!isAuthenticated && (
            <Button
              variant="contained"
              color="secondary"
              size={isXs ? "small" : isSm ? "medium" : "large"}
              onClick={() => history.push(ROUTES.PUBLIC_FORMS)}
            >
              Public Forms
            </Button>
          )}
        </Box>
      </ContentContainer>
    </HeroContainer>
  );
};

export default Hero;
